import React, { useEffect, useRef, useState } from "react";
import { Mic, X, Loader2, Plus } from "lucide-react";
import { api } from "../lib/api";
import { useCart } from "../contexts/CartContext";
import { toast } from "sonner";

/**
 * Push-to-talk Greek voice ordering panel for a SINGLE store.
 * - Hold the mic to record, release to process.
 * - Whisper STT + Claude order parser (POST /api/voice/order).
 */
export const VoicePanel = ({ open, onClose, storeSlug = "grill-bros", storeName = "Store" }) => {
    const { add } = useCart();
    const [phase, setPhase] = useState("idle"); // idle | recording | processing | result
    const [transcript, setTranscript] = useState("");
    const [reply, setReply] = useState("");
    const [items, setItems] = useState([]);
    const [unmatched, setUnmatched] = useState([]);
    const [error, setError] = useState("");

    const mediaRecorderRef = useRef(null);
    const chunksRef = useRef([]);
    const streamRef = useRef(null);
    const audioCtxRef = useRef(null);
    const analyserRef = useRef(null);
    const rafRef = useRef(null);
    const startedAtRef = useRef(0);
    const cancelledRef = useRef(false);
    const safetyTimerRef = useRef(null);
    const [waveform, setWaveform] = useState(Array(14).fill(0));

    useEffect(() => {
        if (!open) {
            cleanup(true);
            setPhase("idle");
            setTranscript(""); setReply(""); setItems([]); setUnmatched([]); setError("");
        }
        return () => cleanup(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const cleanup = (cancel = false) => {
        if (cancel) cancelledRef.current = true;
        try { if (rafRef.current) cancelAnimationFrame(rafRef.current); } catch {}
        try { if (audioCtxRef.current && audioCtxRef.current.state !== "closed") audioCtxRef.current.close(); } catch {}
        try { if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop()); } catch {}
        try { if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current); } catch {}
        rafRef.current = null;
        audioCtxRef.current = null;
        analyserRef.current = null;
        streamRef.current = null;
        safetyTimerRef.current = null;
    };

    const animateWaveform = () => {
        const analyser = analyserRef.current;
        if (!analyser) return;
        const data = new Uint8Array(analyser.frequencyBinCount);
        const tick = () => {
            analyser.getByteFrequencyData(data);
            const bars = 14;
            const step = Math.floor(data.length / bars);
            const next = [];
            for (let i = 0; i < bars; i++) {
                let sum = 0;
                for (let j = 0; j < step; j++) sum += data[i * step + j] || 0;
                next.push(Math.min(100, (sum / step / 255) * 140));
            }
            setWaveform(next);
            rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
    };

    const startRecording = async () => {
        if (phase !== "idle" && phase !== "result") return;
        setError(""); setItems([]); setReply(""); setTranscript(""); setUnmatched([]);
        cancelledRef.current = false;
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;

            try {
                const Ctx = window.AudioContext || window.webkitAudioContext;
                const ctx = new Ctx();
                audioCtxRef.current = ctx;
                const source = ctx.createMediaStreamSource(stream);
                const analyser = ctx.createAnalyser();
                analyser.fftSize = 64;
                source.connect(analyser);
                analyserRef.current = analyser;
                animateWaveform();
            } catch {}

            const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm";
            const mr = new MediaRecorder(stream, { mimeType: mime });
            chunksRef.current = [];
            mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
            mr.onstop = async () => {
                const blob = new Blob(chunksRef.current, { type: mime });
                cleanup(false);
                if (cancelledRef.current) { setPhase("idle"); return; }
                const dur = Date.now() - startedAtRef.current;
                if (blob.size < 1500 || dur < 350) {
                    setError("Δεν άκουσα κάτι, δοκίμασε ξανά");
                    setPhase("idle");
                    return;
                }
                await sendAudio(blob);
            };
            mr.start();
            mediaRecorderRef.current = mr;
            startedAtRef.current = Date.now();
            setPhase("recording");

            safetyTimerRef.current = setTimeout(() => {
                if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
                    try { mediaRecorderRef.current.stop(); } catch {}
                }
            }, 20000);
        } catch (err) {
            setError("Δώσε πρόσβαση στο μικρόφωνο");
            setPhase("idle");
            cleanup(true);
        }
    };

    const stopRecording = (cancel = false) => {
        if (phase !== "recording") return;
        cancelledRef.current = !!cancel;
        const mr = mediaRecorderRef.current;
        if (mr && mr.state === "recording") {
            try { mr.stop(); } catch {}
        }
        setPhase(cancel ? "idle" : "processing");
    };

    const sendAudio = async (blob) => {
        try {
            const formData = new FormData();
            formData.append("audio", blob, "voice.webm");
            const url = `/voice/order?store_slug=${encodeURIComponent(storeSlug)}&session_id=v_${Date.now()}`;
            const { data } = await api.post(url, formData, { headers: { "Content-Type": "multipart/form-data" }, timeout: 30000 });
            setTranscript(data.transcript || "");
            setReply(data.reply || "");
            setItems(data.items || []);
            setUnmatched(data.unmatched || []);
            setPhase("result");
        } catch (err) {
            setError(err.response?.data?.detail || "Σφάλμα μετάφρασης φωνής. Δοκίμασε ξανά.");
            setPhase("idle");
        }
    };

    const addAllToCart = () => {
        items.forEach((it) => {
            const suffix = it.modifiers && it.modifiers.length > 0 ? ` · ${it.modifiers.join(", ")}` : "";
            const variantId = it.modifiers && it.modifiers.length > 0
                ? `${it.id}-mod-${it.modifiers.join("_").replace(/\s+/g, "")}`
                : it.id;
            for (let i = 0; i < it.quantity; i++) {
                add({ id: variantId, name: it.name + suffix, price: it.price, image_url: it.image_url, category: it.category }, { silent: true });
            }
        });
        toast.success(`Έγιναν ${items.reduce((s, i) => s + i.quantity, 0)} προϊόντα στο καλάθι.`);
        onClose();
    };

    if (!open) return null;
    const recording = phase === "recording";

    return (
        <div data-testid="voice-panel" className="fixed inset-0 z-[700] flex items-center justify-center bg-black/85 backdrop-blur-md animate-fade-up" onClick={recording ? null : onClose}>
            <div className="cyber-bg w-full max-w-md mx-4 rounded-3xl border border-[var(--gold)]/40 shadow-[0_0_60px_rgba(244,179,57,0.25)] p-6 relative" onClick={(e) => e.stopPropagation()}>
                <button onClick={onClose} className="absolute top-3 right-3 h-9 w-9 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 z-10" data-testid="voice-close" aria-label="close">
                    <X size={16} />
                </button>

                <div className="text-center mb-5">
                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--gold-light)]">// voice_order</p>
                    <h2 className="font-heading text-2xl mt-1" style={{ fontFamily: '"Cormorant Garamond", serif' }}>{storeName}</h2>
                </div>

                {(phase === "idle" || phase === "recording") && (
                    <div className="text-center space-y-5">
                        {!recording && (
                            <p className="text-sm text-white/70 leading-relaxed">
                                Κράτα πατημένο και πες την παραγγελία.<br/>
                                <span className="font-mono text-[var(--gold-light)] text-xs">"Δύο cheeseburger χωρίς ντομάτα"</span>
                            </p>
                        )}
                        {recording && (
                            <div>
                                <p className="font-heading text-2xl text-white" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
                                    Μίλα τώρα<span className="gold-text animate-pulse">...</span>
                                </p>
                                <div className="flex items-end justify-center gap-1.5 h-12 mt-4">
                                    {waveform.map((h, i) => (
                                        <div key={i} className="w-1.5 rounded-full bg-gradient-to-t from-[var(--gold)] to-[var(--gold-light)] transition-all duration-75" style={{ height: `${Math.max(8, h)}%` }} />
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="relative mx-auto h-32 w-32 grid place-items-center">
                            {recording && <div className="absolute inset-0 rounded-full bg-[var(--gold)]/20 animate-ping" />}
                            {recording && <div className="absolute inset-3 rounded-full bg-[var(--gold)]/15 animate-ping" style={{ animationDelay: "0.4s" }} />}
                            <button
                                data-testid="voice-mic"
                                aria-label="Push to talk"
                                onMouseDown={(e) => { e.preventDefault(); startRecording(); }}
                                onMouseUp={() => stopRecording(false)}
                                onMouseLeave={() => recording && stopRecording(false)}
                                onTouchStart={(e) => { e.preventDefault(); startRecording(); }}
                                onTouchEnd={(e) => { e.preventDefault(); stopRecording(false); }}
                                className={`relative h-28 w-28 grid place-items-center rounded-full select-none touch-none transition ${
                                    recording
                                        ? "bg-gradient-to-br from-[var(--gold)] to-[#c98e15] shadow-[0_0_60px_rgba(244,179,57,0.7)] scale-105"
                                        : "bg-gradient-to-br from-[var(--gold)] to-[#c98e15] shadow-[0_0_40px_rgba(244,179,57,0.5)] active:scale-95"
                                }`}
                            >
                                <Mic size={42} strokeWidth={2.4} className="text-[#0B0B0F]" />
                            </button>
                        </div>
                        <p className="text-xs font-mono uppercase tracking-widest text-white/40">
                            {recording ? "άσε για να σταματήσεις" : "κράτα πατημένο για να μιλήσεις"}
                        </p>
                        {error && <p data-testid="voice-error" className="text-xs text-[var(--gold-light)] mt-2">{error}</p>}
                    </div>
                )}

                {phase === "processing" && (
                    <div className="text-center space-y-4 py-6">
                        <Loader2 className="mx-auto animate-spin text-[var(--gold-light)]" size={42} />
                        <p className="text-sm text-white/70 font-mono">Επεξεργασία...</p>
                    </div>
                )}

                {phase === "result" && (
                    <div className="space-y-4">
                        {transcript && (
                            <div className="rounded-xl bg-white/5 border border-white/10 px-3 py-2">
                                <p className="text-[9px] font-mono uppercase tracking-widest text-white/40">// what i heard</p>
                                <p className="text-sm text-white/85 mt-1">"{transcript}"</p>
                            </div>
                        )}
                        {reply && (
                            <div className="glass-gold rounded-xl px-4 py-3 flex items-start gap-3 border border-[var(--gold)]/25">
                                <div className="h-8 w-8 shrink-0 rounded-full bg-gradient-to-br from-[var(--gold)] to-[#c98e15] grid place-items-center text-[10px] font-mono font-black text-[#0B0B0F]">AI</div>
                                <p className="text-sm text-white/90 leading-relaxed">{reply}</p>
                            </div>
                        )}
                        {items.length > 0 ? (
                            <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                                {items.map((it, i) => (
                                    <div key={i} className="flex gap-3 items-center p-2.5 rounded-lg glass-gold border border-[var(--gold)]/30" data-testid={`voice-item-${i}`}>
                                        <img src={it.image_url} alt={it.name} className="w-12 h-12 object-cover rounded-md" />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-bold truncate">{it.quantity}× {it.name}</p>
                                            {it.modifiers && it.modifiers.length > 0 && (
                                                <p className="text-[10px] text-white/55 italic">{it.modifiers.join(", ")}</p>
                                            )}
                                        </div>
                                        <span className="font-mono text-sm text-[var(--gold-light)]">€{(it.price * it.quantity).toFixed(2)}</span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-center text-xs text-white/40 py-4">Δε βρήκα τίποτα. Δοκίμασε πάλι.</p>
                        )}
                        {unmatched.length > 0 && (
                            <p className="text-[11px] text-[var(--gold-light)] italic">Δεν αναγνώρισα: {unmatched.join(", ")}</p>
                        )}
                        <div className="flex gap-2">
                            <button onClick={() => setPhase("idle")} data-testid="voice-retry" className="flex-1 h-12 rounded-full border-2 border-white/15 hover:border-white/30 font-mono uppercase tracking-widest text-xs transition">
                                Πάλι
                            </button>
                            {items.length > 0 && (
                                <button onClick={addAllToCart} data-testid="voice-add-all" className="flex-1 gold-cta h-12 rounded-full font-mono uppercase tracking-widest text-xs font-bold flex items-center justify-center gap-2">
                                    <Plus size={14} strokeWidth={3} /> Στο καλάθι
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
